import {useLayoutEffect} from "react";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import type {SubscriptionInfo} from "@/api/billing";
import {formatDate} from "@/lib/cabinet";

const MINI_APP_URL =
    (import.meta.env.VITE_MINI_APP_URL as string | undefined) ??
    "https://t.me/leadhold_bot/leadhold";

type ConfirmationState = {
    confirmed?: boolean;
    subscription?: SubscriptionInfo;
};

export function CabinetUnfreezeConfirmedPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const state = location.state as ConfirmationState | null;

    useLayoutEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: "auto"});
    }, []);

    if (!state?.confirmed || state.subscription?.status !== "ACTIVE") {
        return <Navigate to="/cabinet" replace/>;
    }

    const nextChargeAt = formatDate(state.subscription.next_charge_at, {
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    return (
        <section className="cabinet-unfreeze-complete-flow" aria-labelledby="unfreeze-complete-title">
            <article className="cabinet-panel cabinet-unfreeze-complete-card">
                <div className="cabinet-unfreeze-complete-visual" aria-hidden="true">
                    <span className="cabinet-unfreeze-complete-orbit cabinet-unfreeze-complete-orbit-one"/>
                    <span className="cabinet-unfreeze-complete-orbit cabinet-unfreeze-complete-orbit-two"/>
                    <span className="cabinet-unfreeze-complete-mark">
                        <svg viewBox="0 0 52 52">
                            <path d="m15 27 7 7 15-17"/>
                        </svg>
                    </span>
                    <span className="cabinet-unfreeze-complete-spark spark-one"/>
                    <span className="cabinet-unfreeze-complete-spark spark-two"/>
                    <span className="cabinet-unfreeze-complete-spark spark-three"/>
                </div>

                <div className="cabinet-unfreeze-complete-copy">
                    <span className="cabinet-cancel-eyebrow">Подписка возобновлена</span>
                    <h2 id="unfreeze-complete-title">С возвращением!</h2>
                    <p>
                        Доступ к Лидхолду уже активен. Кампании, диалоги,
                        CRM и настройки AI-агента сохранены и снова доступны.
                    </p>
                </div>

                <div className="cabinet-unfreeze-complete-status" aria-label="Статус подписки">
                    <div>
                        <span>Доступ к Лидхолду</span>
                        <strong className="is-active"><i aria-hidden="true"/>Активен</strong>
                        <small>Можно продолжить работу прямо сейчас.</small>
                    </div>
                    <div>
                        <span>Следующее списание</span>
                        <strong>
                            {nextChargeAt ? (
                                <time dateTime={state.subscription.next_charge_at ?? undefined}>{nextChargeAt}</time>
                            ) : "По графику тарифа"}
                        </strong>
                        <small>Возобновление не вызвало мгновенного списания.</small>
                    </div>
                </div>

                <div className="cabinet-unfreeze-complete-note">
                    <span aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                            <path d="M4 17V7a3 3 0 0 1 3-3h10a3 3 0 0 1 3 3v10a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3Z"/>
                            <path d="m8 12 2.5 2.5L16 9"/>
                        </svg>
                    </span>
                    <div>
                        <strong>Всё готово к работе</strong>
                        <p>Мы сняли паузу с подписки и вернули доступ ко всем инструментам.</p>
                    </div>
                </div>

                <div className="cabinet-unfreeze-complete-actions">
                    <a className="btn btn-primary" href={MINI_APP_URL}>
                        Открыть Лидхолд
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 7 8 5-8 5Z"/></svg>
                    </a>
                    <button type="button" className="btn btn-muted" onClick={() => navigate("/cabinet")}>
                        Вернуться в кабинет
                    </button>
                </div>
            </article>
        </section>
    );
}
